import React, { useState, useEffect } from "react";
import { 
  Box, 
  Button, 
  TextField,
  Typography, 
  CircularProgress, 
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  Container,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useClerk, useSignUp, useAuth, useSignIn } from "@clerk/clerk-react";
import Header from "../common/Header";
import { Visibility, VisibilityOff, AdminPanelSettings } from "@mui/icons-material";
import { ENTERPRISE_SETUP_CODE, API_URL } from "../../config";
import { registerEnterpriseAdmin, checkAdminExists } from "../../services/adminService";

const AdminRegistration: React.FC = () => {
  const navigate = useNavigate();
  
  // Check if user is already signed in
  const { isSignedIn, userId } = useAuth();
  
  // Stepper state
  const [activeStep, setActiveStep] = useState<number>(0);
  const steps = ['Create Admin Account', 'Organization Setup', 'Confirmation'];
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Setup code (ensure only authorized users can create admin account)
  const [setupCode, setSetupCode] = useState('');
  
  // Admin existence check
  const [adminExists, setAdminExists] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  
  // Clerk hooks
  const { signUp, isLoaded: isSignUpLoaded } = useSignUp();
  const { signIn, isLoaded: isSignInLoaded } = useSignIn();
  const clerk = useClerk();

  // Special state for handling existing Clerk users
  const [existingClerkUser, setExistingClerkUser] = useState(false);
  const [existingUserInfo, setExistingUserInfo] = useState<{email: string}>({email: ''});

  // Check if admin already exists when component mounts
  useEffect(() => {
    const verifyAdminState = async () => {
      try {
        setCheckingAdmin(true);
        // Check if an admin user already exists
        const adminExistsResult = await checkAdminExists();
        
        setAdminExists(adminExistsResult.exists);
        
        if (adminExistsResult.exists) {
          // If admin exists and user is signed in, check if they are an admin
          if (isSignedIn && userId) {
            // If they're already signed in, redirect to dashboard
            navigate('/dashboard');
          } else {
            // If not signed in, redirect to login
            navigate('/login');
          }
        }
      } catch (error) {
        console.error('Error checking admin existence:', error);
        // Default to showing the form if there's an error checking
        setAdminExists(false);
      } finally {
        setCheckingAdmin(false);
        setLoading(false);
      }
    };

    verifyAdminState();
  }, [isSignedIn, userId, navigate]);

  // Add a second useEffect to check if the user is already signed in
  // and redirect to the dashboard
  useEffect(() => {
    if (isSignedIn && userId) {
      // If we get here and the user is already signed in,
      // redirect them to the dashboard
      navigate('/dashboard');
    }
  }, [isSignedIn, userId, navigate]);

  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Validate admin registration form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (activeStep === 0) {
      if (!setupCode.trim()) {
        errors.setupCode = 'Setup code is required';
        isValid = false;
      } else if (setupCode !== ENTERPRISE_SETUP_CODE) {
        errors.setupCode = 'Invalid setup code';
        isValid = false;
      }

      if (!email.trim()) {
        errors.email = 'Email is required';
        isValid = false;
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
        errors.email = 'Invalid email address';
        isValid = false;
      }

      if (!password) {
        errors.password = 'Password is required';
        isValid = false;
      } else if (password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
        isValid = false;
      }

      if (password !== confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
        isValid = false;
      }

      if (!firstName.trim()) {
        errors.firstName = 'First name is required';
        isValid = false;
      }

      if (!lastName.trim()) {
        errors.lastName = 'Last name is required';
        isValid = false;
      }
    } else if (activeStep === 1) {
      if (!organizationName.trim()) {
        errors.organizationName = 'Organization name is required';
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle next step
  const handleNext = () => {
    if (validateForm()) {
      if (activeStep === steps.length - 1) {
        handleRegister();
      } else {
        setActiveStep((prevStep) => prevStep + 1);
      }
    }
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Handle admin registration
  const handleRegister = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // First verify if admin already exists once more
      const adminExistsResult = await checkAdminExists();
      if (adminExistsResult.exists) {
        setError('An admin account already exists. Please log in or contact support.');
        setLoading(false);
        return;
      }

      // Check if user is already signed in (exists in Clerk)
      if (isSignedIn && userId) {
        console.log('User already exists in Clerk. Registering in backend database...');
        
        // Create the enterprise admin account through our API using existing Clerk ID
        try {
          const adminResponse = await registerEnterpriseAdmin({
            email,
            firstName,
            lastName,
            organizationName,
            clerkId: userId
          });
          
          console.log('Admin registered in backend:', adminResponse);
          
          // Add the user to the organization (ensuring the admin is associated with the org)
          try {
            const response = await fetch(`${API_URL}/users/set-organization`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                userId: userId,
                organizationId: adminResponse.organizationId 
              })
            });
            
            if (!response.ok) {
              console.warn('Warning: User was created but may not be properly associated with organization');
            } else {
              console.log('Admin successfully associated with organization');
            }
          } catch (orgError) {
            console.warn('Warning: Error associating user with organization', orgError);
          }
          
          // Redirect to dashboard
          navigate('/dashboard');
          return;
        } catch (backendError: unknown) {
          console.error('Backend registration error:', backendError);
          const errorMessage = backendError instanceof Error 
            ? backendError.message 
            : 'Error creating admin account. Please try again.';
          setError(errorMessage);
          setLoading(false);
          return;
        }
      }
      
      // If the user doesn't exist in Clerk yet, continue with sign-up process
      if (!signUp) {
        setError('System error: Unable to initialize the registration process.');
        return;
      }
      
      try {
        // Start the sign-up process with Clerk
        const signUpAttempt = await signUp!.create({
          emailAddress: email,
          password,
          firstName,
          lastName,
          // Add metadata to indicate this is an admin account
          unsafeMetadata: {
            role: 'admin',
            isEnterprise: true
          }
        });
        
        // Handle verification steps first
        if (signUpAttempt.status === 'missing_requirements' && 
            signUpAttempt.unverifiedFields.includes('email_address')) {
          
          // Save organization data for after verification
          localStorage.setItem('enterprise_org_data', JSON.stringify({
            organizationName,
            firstName,
            lastName,
            email
          }));
          
          // Prepare email verification
          await signUp!.prepareEmailAddressVerification({
            strategy: 'email_code'
          });
          
          // Redirect to verification page
          navigate('/signup/verify', { state: { redirectToDashboard: true } });
          return;
        }
        
        // If we don't have a user ID yet, stop and show error
        if (!signUpAttempt.createdUserId) {
          console.error('Sign-up response:', signUpAttempt);
          setError('Account creation incomplete. Please verify your email to continue.');
          setLoading(false);
          return;
        }
        
        console.log('Clerk user created. Registering in backend database...');
        
        // Create the enterprise admin account through our API
        try {
          const adminResponse = await registerEnterpriseAdmin({
            email,
            firstName,
            lastName,
            organizationName,
            clerkId: signUpAttempt.createdUserId
          });
          
          console.log('Admin registered in backend:', adminResponse);
          
          // Add the user to the organization (ensuring the admin is associated with the org)
          try {
            const response = await fetch(`${API_URL}/users/set-organization`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                userId: signUpAttempt.createdUserId,
                organizationId: adminResponse.organizationId 
              })
            });
            
            if (!response.ok) {
              console.warn('Warning: User was created but may not be properly associated with organization');
            } else {
              console.log('Admin successfully associated with organization');
            }
          } catch (orgError) {
            console.warn('Warning: Error associating user with organization', orgError);
          }
        } catch (backendError: unknown) {
          console.error('Backend registration error:', backendError);
          // Continue with the sign-in process even if backend registration failed
          // The verification step will try again to create the user if needed
        }
        
        // Check if we have a session already
        if (signUpAttempt.createdSessionId) {
          // Sign in the user automatically
          await clerk.setActive({
            session: signUpAttempt.createdSessionId
          });
          
          // Redirect to dashboard
          navigate('/dashboard');
        } else {
          // Redirect to verification page if needed
          navigate('/signup/verify', { state: { redirectToDashboard: true } });
        }
      } catch (clerkError: unknown) {
        // Check if this is the "email taken" error
        const errorMsg = clerkError instanceof Error ? clerkError.message : String(clerkError);
        console.error('Clerk error:', errorMsg);
        
        if (errorMsg.includes('email address is taken') || errorMsg.includes('identifier already exists')) {
          console.log('Email already exists in Clerk. Attempting to sign in with provided password first...');
          
          try {
            if (!signIn) {
              setError('System error: Unable to initialize the sign-in process.');
              setLoading(false);
              return;
            }
            
            // First try to sign in with the email and password the user already provided
            const signInAttempt = await signIn.create({
              identifier: email,
              password,
            });
            
            // If sign-in is successful with the provided password
            if (signInAttempt.status === 'complete' && signInAttempt.createdSessionId) {
              // Set the active session
              await clerk.setActive({
                session: signInAttempt.createdSessionId
              });
              
              // Get the current user ID after signing in
              const currentUserId = clerk.user?.id || userId || '';
              
              // Now register in the backend with the existing Clerk user
              try {
                const adminResponse = await registerEnterpriseAdmin({
                  email,
                  firstName,
                  lastName,
                  organizationName,
                  clerkId: currentUserId
                });
                
                console.log('Admin registered in backend with existing Clerk account:', adminResponse);
                
                // Add the user to the organization (ensuring the admin is associated with the org)
                try {
                  const response = await fetch(`${API_URL}/users/set-organization`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                      userId: currentUserId,
                      organizationId: adminResponse.organizationId 
                    })
                  });
                  
                  if (!response.ok) {
                    console.warn('Warning: User was created but may not be properly associated with organization');
                  } else {
                    console.log('Admin successfully associated with organization');
                  }
                } catch (orgError) {
                  console.warn('Warning: Error associating user with organization', orgError);
                }
                
                // Redirect to dashboard
                navigate('/dashboard');
                return;
              } catch (backendError: unknown) {
                console.error('Backend registration error:', backendError);
                const errorMessage = backendError instanceof Error 
                  ? backendError.message 
                  : 'Error creating admin account. Please try again.';
                setError(errorMessage);
                setLoading(false);
                return;
              }
            } else if (signInAttempt.status === 'needs_second_factor') {
              // Handle 2FA
              setError('Two-factor authentication required. Please use the same email and password you originally registered with.');
              // Show the password entry UI for 2FA cases
              setExistingClerkUser(true);
              setExistingUserInfo({email});
              setLoading(false);
              return;
            } else {
              // Initial sign-in attempt failed, switch to password entry UI
              console.log('Initial sign-in attempt failed. Showing password entry form.');
              setExistingClerkUser(true);
              setExistingUserInfo({email});
              setError(null);
              setLoading(false);
              // Clear password fields for security
              setPassword('');
              setConfirmPassword('');
              return;
            }
          } catch (signInError: unknown) {
            // Sign-in attempt failed, show password entry UI
            console.error('Sign-in error:', signInError);
            setExistingClerkUser(true);
            setExistingUserInfo({email});
            setError(null);
            setLoading(false);
            // Clear password fields for security
            setPassword('');
            setConfirmPassword('');
            return;
          }
        } else {
          // Other Clerk error
          const errorMessage = clerkError instanceof Error 
            ? clerkError.message 
            : 'Error creating admin account. Please try again.';
          setError(errorMessage);
          setLoading(false);
        }
      }
    } catch (error: unknown) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Error creating admin account. Please try again.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  // Handle next step for existing Clerk user login
  const handleExistingUserLogin = async () => {
    if (!validatePassword()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!signIn) {
        setError('System error: Unable to initialize the sign-in process.');
        setLoading(false);
        return;
      }
      
      // Try to sign in with the provided credentials
      const signInAttempt = await signIn.create({
        identifier: existingUserInfo.email || email,
        password,
      });
      
      if (signInAttempt.status === 'complete' && signInAttempt.createdSessionId) {
        // Set the active session
        await clerk.setActive({
          session: signInAttempt.createdSessionId
        });
        
        // Get the current user ID after signing in
        const currentUserId = clerk.user?.id || userId || '';
        
        // Now register in the backend with the existing Clerk user
        try {
          const adminResponse = await registerEnterpriseAdmin({
            email: existingUserInfo.email || email,
            firstName,
            lastName,
            organizationName,
            clerkId: currentUserId
          });
          
          console.log('Admin registered in backend with existing Clerk account:', adminResponse);
          
          // Add the user to the organization (ensuring the admin is associated with the org)
          try {
            const response = await fetch(`${API_URL}/users/set-organization`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                userId: currentUserId,
                organizationId: adminResponse.organizationId 
              })
            });
            
            if (!response.ok) {
              console.warn('Warning: User was created but may not be properly associated with organization');
            } else {
              console.log('Admin successfully associated with organization');
            }
          } catch (orgError) {
            console.warn('Warning: Error associating user with organization', orgError);
          }
          
          // Redirect to dashboard
          navigate('/dashboard');
          return;
        } catch (backendError: unknown) {
          console.error('Backend registration error:', backendError);
          const errorMessage = backendError instanceof Error 
            ? backendError.message 
            : 'Error creating admin account. Please try again.';
          setError(errorMessage);
          setLoading(false);
          return;
        }
      } else if (signInAttempt.status === 'needs_second_factor') {
        // Handle 2FA if needed
        setError('Two-factor authentication required. Please use the same email and password you originally registered with.');
        setLoading(false);
        return;
      } else {
        // Failed to sign in
        setError('Incorrect password. Please use the password you originally registered with.');
        setLoading(false);
        return;
      }
    } catch (signInError: unknown) {
      // Failed to sign in
      console.error('Sign in error:', signInError);
      const errorMsg = signInError instanceof Error ? signInError.message : String(signInError);
      
      // Provide more helpful error message
      if (errorMsg.includes('incorrect password') || errorMsg.includes('failed to authenticate')) {
        setError('Incorrect password. Please use the password you originally registered with.');
      } else {
        setError('Authentication failed. Please check your credentials and try again.');
      }
      
      setLoading(false);
      return;
    }
  };

  // Validate just the password field for existing users
  const validatePassword = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  if (!isSignUpLoaded || checkingAdmin) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (adminExists) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f4f6f8"
        }}
      >
        <Header />
        <Container maxWidth="md" sx={{ py: 6, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="primary">
              Admin Already Registered
            </Typography>
            <Typography variant="body1" paragraph>
              An administrator account has already been set up for this application.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                  backgroundColor: "#5D5FEF",
                  '&:hover': { backgroundColor: "#4A4BD5" }
                }}
              >
                Go to Login
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f4f6f8"
      }}
    >
      <Header />

      <Container maxWidth="md" sx={{ py: 6, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {/* Enterprise Setup Card */}
        <Card 
          sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, #5D5FEF 0%, #4A4BD5 100%)',
            color: 'white',
            boxShadow: '0px 8px 20px rgba(93, 95, 239, 0.25)',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <AdminPanelSettings sx={{ fontSize: 40, mr: 2 }} />
              <Typography variant="h4" fontWeight="bold">Enterprise Admin Setup</Typography>
            </Box>
            <Typography variant="body1">
              Create your administrator account to manage your organization's Tranzcribe Enterprise instance.
            </Typography>
          </CardContent>
        </Card>

        <Paper 
          elevation={3} 
          sx={{ p: { xs: 3, md: 4 }, borderRadius: 2 }}
        >
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {existingClerkUser && (
            <Box sx={{ mb: 4 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                This email is already registered in our system. Please enter your existing password to continue.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Sign in with Existing Account
              </Typography>
              
              <TextField
                fullWidth
                label="Email Address"
                value={existingUserInfo.email || email}
                disabled
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Your Existing Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  onClick={() => {
                    setExistingClerkUser(false);
                    setError(null);
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleExistingUserLogin}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#5D5FEF",
                    color: "#fff",
                    '&:hover': {
                      backgroundColor: "#4A4BD5",
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Continue'
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {!existingClerkUser && (
            <>
              {activeStep === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Admin Account Details
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Enterprise Setup Code"
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value)}
                    error={!!formErrors.setupCode}
                    helperText={formErrors.setupCode}
                    margin="normal"
                    required
                  />
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      error={!!formErrors.firstName}
                      helperText={formErrors.firstName}
                      margin="normal"
                      required
                    />
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      error={!!formErrors.lastName}
                      helperText={formErrors.lastName}
                      margin="normal"
                      required
                    />
                  </Box>
                  
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    margin="normal"
                    required
                  />
                  
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    margin="normal"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleTogglePassword} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={!!formErrors.confirmPassword}
                    helperText={formErrors.confirmPassword}
                    margin="normal"
                    required
                  />
                </Box>
              )}

              {activeStep === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Organization Setup
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Organization Name"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    error={!!formErrors.organizationName}
                    helperText={formErrors.organizationName}
                    margin="normal"
                    required
                  />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    This information will be used to set up your enterprise account. You can customize additional settings after registration.
                  </Typography>
                </Box>
              )}

              {activeStep === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Confirm Your Details
                  </Typography>
                  
                  <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 3 }}>
                    <Typography variant="subtitle2">Admin Account</Typography>
                    <Typography variant="body2">{firstName} {lastName}</Typography>
                    <Typography variant="body2">{email}</Typography>
                    
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Organization</Typography>
                    <Typography variant="body2">{organizationName}</Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    By clicking "Complete Setup", you agree to our Terms of Service and Privacy Policy. Your enterprise account will be created with administrator privileges.
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === 0 || loading}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#5D5FEF",
                    color: "#fff",
                    py: 1.5,
                    px: 3,
                    '&:hover': {
                      backgroundColor: "#4A4BD5",
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : activeStep === steps.length - 1 ? (
                    'Complete Setup'
                  ) : (
                    'Next'
                  )}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminRegistration; 